import React, { Fragment, useState, useEffect } from 'react'
import {
  Box,
  Button,
  Grid,
  IconButton,
  LinearProgress,
  Tooltip,
  Typography,
  
  Link,
  CircularProgress,
  TextField,
  MenuItem,
  ListItemText,
  FormControlLabel,
  Checkbox
} from '@mui/material'
import { withStyles } from '@mui/styles'
import { uploadInternalOrderFile, downloadTemplate } from '../config/api'
import { useCourier } from '../../../hooks/useCourier'
import { useUser } from '../../../hooks/useUser'
import { useAuth } from 'oidc-react'
import { Courier, CourierService } from '../../../interfaces/Courier'
import {
  CityProvince,
  NeighborhoodComuna,
  StatesRegion
} from '../../../interfaces/Location/Location'
import { stateRegion } from '../../../utils/states-region'
import { provinces } from '../../../utils/city-provincia'
import { cities } from '../../../utils/neighborhood-comuna'
import {
  configDateInitial,
  localDateFromInput
} from '../../../utils/dateHelper'
import { OrderOriginsDDL } from '../../../components/OrderOrigin/OrderOriginsDDL'
import HelpIcon from '@mui/icons-material/Help'
import { templateMassiveProcessInternalOrder } from '../../../const/templatesFileName'

const BorderLinearProgress = withStyles(() => ({
  root: {
    height: 15,
    borderRadius: 5
  },
  colorPrimary: {
    backgroundColor: '#EEEEEE'
  },
  bar: {
    borderRadius: 5,
    backgroundColor: '#1a90ff'
  }
}))(LinearProgress)

interface InternalOrderUploadProps {
  onSuccess: (message: string) => void
  onError: (message: string) => void
}

interface OrderFormData {
  courierDeliveryDate: string
  courierId: number
  courierServiceId: number
  neighborhoodId: number
  stateId: number
  cityId: number
  userId: number
  rtv: boolean
  comments: string
  orderOriginId: number
}

export const InternalOrderUpload: React.FC<InternalOrderUploadProps> = ({
  onSuccess,
  onError
}) => {
  const maxFileSize = 2097152 // 2 MB

  const auth = useAuth()
  const isAndina = auth.userData?.profile.tenant === 'AndinaCL'

  const {
    userDataState: { userData }
  } = useUser()

  const initialFormData: OrderFormData = {
    courierDeliveryDate: configDateInitial(0),
    courierId: -1,
    courierServiceId: -1,
    neighborhoodId: !isAndina ? 1 : -1,
    stateId: !isAndina ? 1 : -1,
    cityId: !isAndina ? 1 : -1,
    userId: userData?.userId || 0,
    rtv: false,
    comments: '',
    orderOriginId: 0
  }

  const [states, setState] = useState<StatesRegion[]>([])
  const [city, setCity] = useState<CityProvince[]>([])
  const [neighborhood, setNeighborhood] = useState<NeighborhoodComuna[]>([])
  const [isUploading, setIsUploading] = useState<boolean>(false)
  const [selectedFiles, setSelectedFiles] = useState<FileList | null>(null)
  const [currentFile, setCurrentFile] = useState<File | null>(null)
  const [progress, setProgress] = useState<number>(0)
  const [disabledLink, setDisabledLink] = useState<boolean>(false)
  const [formData, setFormData] = useState<OrderFormData>(initialFormData)
  const [isRTV, setIsRTV] = useState<boolean>(initialFormData.rtv)

  const {
    couriersState: { couriers },
    getCouriers
  } = useCourier()

  useEffect(() => {
    if (couriers?.length === 0) {
      getCouriers()
    }
  }, [])

  useEffect(() => {
    setCity(provinces)
    setNeighborhood(cities)
    setState(stateRegion as [])
  }, [])

  useEffect(() => {
    setFormData({
      ...formData,
      cityId: -1,
      neighborhoodId: -1
    })
  }, [formData.stateId])

  useEffect(() => {
    setFormData({
      ...formData,
      courierServiceId: -1
    })
  }, [formData.courierId])

  useEffect(() => {
    setFormData((prevState) => ({
      ...prevState,
      rtv: isRTV
    }))
  }, [isRTV])

  const handleFormChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [event.target.name]: event.target.value })
  }

  const handleSelectFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedFiles(event.target.files)
    setProgress(0)
  }

  const sizeLabel = (size: number): string => {
    if (size / 1024 / 1024 > 1) return (size / 1024 / 1024).toFixed(2) + ' MB'
    if (size / 1024 > 1) return (size / 1024).toFixed(0) + ' KB'
    return size.toFixed(2) + ' Bytes'
  }

  const validateFile = (files: FileList | null): string => {
    if (!files || files.length === 0) {
      return 'Ningún archivo seleccionado'
    }

    const selectedFile = files[0]

    if (selectedFile.size > maxFileSize) {
      const maxLabel = sizeLabel(maxFileSize)
      return `El archivo seleccionado supera el tamaño máximo: ${maxLabel}`
    }

    if (selectedFile.name.toLowerCase().indexOf('.xls') < 0) {
      return 'El archivo seleccionado no es de tipo Excel (.xls)'
    }

    return ''
  }

  const handleUpload = async () => {
    const validateError = validateFile(selectedFiles)
    if (validateError !== '') {
      setCurrentFile(null)
      onError(validateError)
      return
    }

    if (!selectedFiles) return

    const selectedFile = selectedFiles[0]
    setProgress(0)
    setCurrentFile(selectedFile)
    setIsUploading(true)

    const orderData = {
      ...formData,
      courierDeliveryDate: localDateFromInput(formData.courierDeliveryDate),
      userId: userData?.userId || 0
    }

    try {
      const response = await uploadInternalOrderFile(selectedFile, orderData)

      setIsUploading(false)

      if (response.data.success) {
        onSuccess('El archivo ha sido cargado para su procesamiento')
      } else {
        onError(
          `Error en carga de archivo: ${
            response.data.message || 'falla en el servicio.'
          }`
        )
      }

      setCurrentFile(null)
      setSelectedFiles(null)
      setProgress(0)
    } catch (error) {
      setIsUploading(false)
      setProgress(0)
      onError(
        `Error en carga de archivo: ${
          (error as any)?.data?.message || 'falla en el servicio.'
        }`
      )
      setCurrentFile(null)
      setSelectedFiles(null)
      setProgress(0)
    }
  }

  const handleDownloadTemplate = async () => {
    setDisabledLink(true)

    try {
      const response = await downloadTemplate(
        templateMassiveProcessInternalOrder
      )

      if (response.status === 200 && response.data !== null) {
        const raw = window.atob(response.data.file)
        const uInt8Array = new Uint8Array(raw.length)
        for (let i = 0; i < raw.length; ++i) {
          uInt8Array[i] = raw.charCodeAt(i)
        }
        const link = document.createElement('a')
        const blob = new Blob([uInt8Array], {
          type: response.data.mimeType
        })
        link.style.display = 'none'
        link.href = URL.createObjectURL(blob)
        link.setAttribute('download', response.data.name)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      }
    } catch (error) {
      console.error('Error descargando plantilla:', error)
      onError('Error al descargar la plantilla')
    } finally {
      setDisabledLink(false)
    }
  }

  const courierServices = () => {
    let courierServicesList: { name: string; value: string }[] = []

    if (
      couriers !== undefined &&
      couriers.length > 0 &&
      formData.courierId !== -1 &&
      couriers.filter((f) => f.courierId === formData.courierId).length > 0
    ) {
      courierServicesList = couriers
        ?.filter((f) => f.courierId === formData.courierId)[0]
        .courierServices.map((opt: CourierService) => {
          return {
            value: `${opt.courierServiceId}`,
            name: `${opt.name}`
          }
        })
    }

    return courierServicesList
  }

  return (
    <Fragment>
      <Grid container spacing={4} direction="column" alignItems="center">
        <Typography variant="h4" component="h4" style={{ padding: '10px' }}>
          Crear pedido interno
        </Typography>

        {isAndina && (
          <Grid container item xs={12} spacing={3}>
            <Grid item xs={12} sm={4}>
              <TextField
                select
                fullWidth
                label="Región"
                id="stateId"
                name="stateId"
                onChange={handleFormChange}
                variant="outlined"
                value={formData.stateId}
                error={formData.stateId === -1}
                helperText={
                  formData.stateId === -1 ? 'Debe seleccionar una región' : ''
                }
              >
                <MenuItem key={-1} value={-1}>
                  Seleccione Región
                </MenuItem>
                {states &&
                  states.map((opt: StatesRegion) => (
                    <MenuItem key={opt.stateCode} value={opt.stateCode}>
                      {opt.name}
                    </MenuItem>
                  ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                select
                fullWidth
                label="Provincia"
                id="cityId"
                name="cityId"
                onChange={handleFormChange}
                variant="outlined"
                value={formData.cityId}
                error={formData.cityId === -1}
                helperText={
                  formData.cityId === -1 ? 'Debe seleccionar una provincia' : ''
                }
              >
                <MenuItem key={-1} value={-1}>
                  Seleccione Provincia
                </MenuItem>
                {city &&
                  city
                    .filter((item) => item.stateId === formData.stateId)
                    .map((opt: CityProvince) => (
                      <MenuItem key={opt.cityId} value={opt.cityId}>
                        {opt.name}
                      </MenuItem>
                    ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                select
                fullWidth
                id="neighborhoodId"
                name="neighborhoodId"
                label="Comuna"
                onChange={handleFormChange}
                variant="outlined"
                value={formData.neighborhoodId}
                error={formData.neighborhoodId === -1}
                helperText={
                  formData.neighborhoodId === -1
                    ? 'Debe seleccionar una comuna'
                    : ''
                }
              >
                <MenuItem key={-1} value={-1}>
                  Seleccione Comuna
                </MenuItem>
                {neighborhood &&
                  neighborhood
                    .filter(
                      (item: NeighborhoodComuna) =>
                        item.cityId === formData.cityId
                    )
                    .map((opt: NeighborhoodComuna) => (
                      <MenuItem
                        key={opt.neighborhoodId}
                        value={opt.neighborhoodId}
                      >
                        {opt.name}
                      </MenuItem>
                    ))}
              </TextField>
            </Grid>
          </Grid>
        )}

        <Grid container item xs={12} spacing={3}>
          <Grid item xs={12} sm={4}>
            <TextField
              type="date"
              fullWidth
              label="Fecha Entrega Courier"
              onChange={handleFormChange}
              id="courierDeliveryDate"
              name="courierDeliveryDate"
              variant="outlined"
              value={formData.courierDeliveryDate?.toString().split('T')[0]}
              error={formData.courierDeliveryDate.length === 0}
              helperText={
                formData.courierDeliveryDate.length === 0
                  ? 'Debe ingresar fecha entrega courier'
                  : ''
              }
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              select
              fullWidth
              label="Courier"
              onChange={handleFormChange}
              id="courierId"
              name="courierId"
              value={formData.courierId}
              variant="outlined"
              error={formData.courierId === -1}
              helperText={
                formData.courierId === -1 ? 'Debe seleccionar un courier' : ''
              }
            >
              <MenuItem key={-1} value={-1}>
                Seleccione Courier
              </MenuItem>
              {couriers &&
                couriers.map((opt: Courier) => (
                  <MenuItem key={opt.courierId} value={opt.courierId}>
                    {opt.name}
                  </MenuItem>
                ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              select
              fullWidth
              label="Servicio"
              onChange={handleFormChange}
              id="courierServiceId"
              name="courierServiceId"
              variant="outlined"
              value={formData.courierServiceId}
              error={formData.courierServiceId === -1}
              helperText={
                formData.courierServiceId === -1
                  ? 'Debe seleccionar un servicio'
                  : ''
              }
            >
              <MenuItem key={-1} value={-1}>
                Seleccione Servicio
              </MenuItem>
              {courierServices().map((el) => (
                <MenuItem key={el.value} value={el.value}>
                  {el.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6}>
            <OrderOriginsDDL
              onChange={handleFormChange}
              value={formData.orderOriginId || 0}
              type="outlined"
              defaultLabel="Seleccione un origen de pedido..."
              isSearching={false}
              fullWidth
              error={formData.orderOriginId === 0}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isRTV}
                  value={isRTV}
                  onChange={() => setIsRTV((prevState) => !prevState)}
                  name="chkRtv"
                  color="primary"
                />
              }
              label="Para Staging de Salida (RTV)"
            />
          </Grid>
        </Grid>

        <Grid container item xs={12} spacing={3}>
          <Grid item xs={12} sm={12}>
            <TextField
              fullWidth
              name="comments"
              id="comments"
              label="Observaciones"
              multiline
              rows={3}
              variant="outlined"
              InputLabelProps={{ shrink: true }}
              onChange={handleFormChange}
              value={formData.comments}
            />
          </Grid>
          <Grid
            item
            xs={12}
            sm={12}
            style={{ padding: '0px', marginBottom: '25px' }}
          >
            <ListItemText
              primary={''}
              secondary={
                <Box>
                  <Typography component="ol" style={{ listStyle: 'square' }}>
                    <Typography variant="subtitle1" component="li" gutterBottom>
                      Puede descargar una <b>plantilla</b> desde el link que se
                      encuentra disponible a continuación.
                      <br />
                      {disabledLink ? (
                        <Fragment>
                          <Link
                            style={{
                              cursor: 'pointer',
                              pointerEvents: 'none'
                            }}
                            onClick={handleDownloadTemplate}
                          >
                            Descargando Plantilla...
                          </Link>{' '}
                          <CircularProgress size="0.9rem" />
                        </Fragment>
                      ) : (
                        <Link
                          style={{ cursor: 'pointer' }}
                          onClick={handleDownloadTemplate}
                        >
                          Descargar Plantilla
                        </Link>
                      )}
                    </Typography>
                  </Typography>
                </Box>
              }
            />
          </Grid>
        </Grid>
      </Grid>

      <Box className="mb25" display="flex" alignItems="center">
        <Grid
          container
          spacing={4}
          direction="row"
          alignItems="center"
          justifyContent="center"
          style={{ justifyContent: 'center' }}
        >
          <Grid item xs={3} style={{ textAlign: 'right' }}>
            <label htmlFor="btn-upload-internal">
              <input
                id="btn-upload-internal"
                name="btn-upload-internal"
                style={{ display: 'none' }}
                type="file"
                onChange={handleSelectFile}
                accept=".xls,.xlsx"
              />
              <Tooltip
                title="Seleccionar el archivo Excel a cargar"
                placement="top-end"
              >
                <Button
                  className="btn-choose"
                  variant="outlined"
                  component="span"
                >
                  Seleccionar Archivo
                </Button>
              </Tooltip>
            </label>
          </Grid>

          <Grid item xs={5}>
            <div className="file-name">
              {selectedFiles && selectedFiles.length > 0
                ? selectedFiles[0].name
                : null}
            </div>
          </Grid>

          <Grid item xs={1}>
            <Tooltip
              title="Enviar el archivo seleccionado al servidor para su procesamiento"
              placement="top-end"
            >
              <Button
                className="btn-upload"
                color="primary"
                variant="contained"
                component="span"
                disabled={
                  !selectedFiles ||
                  isUploading ||
                  formData.stateId === -1 ||
                  formData.cityId === -1 ||
                  formData.neighborhoodId === -1 ||
                  formData.courierId === -1 ||
                  formData.courierServiceId === -1 ||
                  formData.orderOriginId <= 0
                }
                onClick={handleUpload}
              >
                Cargar
              </Button>
            </Tooltip>
          </Grid>

          <Grid
            item
            xs={1}
            alignContent="center"
            style={{ textIndent: '15px' }}
          >
            <Tooltip
              title="Oprima el botón SELECCIONAR para abrir el archivo a cargar y luego el botón CARGAR para enviar el archivo al servidor para su procesamiento"
              placement="top-end"
            >
              <IconButton>
                <HelpIcon />
              </IconButton>
            </Tooltip>
          </Grid>
        </Grid>
      </Box>

      <div
        style={{
          margin: '10px',
          visibility: currentFile ? 'visible' : 'hidden'
        }}
      >
        <BorderLinearProgress variant="determinate" value={progress} />

        <Box minWidth={35}>
          <Typography
            variant="body1"
            color="textSecondary"
            style={{ textAlign: 'end' }}
          >{`${progress}%`}</Typography>
        </Box>
      </div>
    </Fragment>
  )
}
